import { useState } from 'react'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { ListPageRowAction } from '@/components/templates/list-page'
import { connectionPath, type Connection } from './types'
import type { ConnectionCommand, useConnectionAction } from './useConnectionsList'

export function connectionActions(
  row: Connection,
  run: (command: ConnectionCommand) => void,
  edit: (command: ConnectionCommand) => void,
): ListPageRowAction<Connection>[] {
  if (['removed', 'unavailable'].includes(row.status)) return []
  if (row.accounting_bridge)
    return [
      { label: 'Accounting settings', onSelect: () => window.location.assign(connectionPath(row)) },
    ]
  const paused = row.status === 'paused'
  return [
    { label: 'Sync now', disabled: paused, onSelect: () => run({ row, action: 'sync_now' }) },
    {
      label: paused ? 'Resume' : 'Pause',
      onSelect: () => run({ row, action: paused ? 'resume' : 'pause' }),
    },
    { label: 'Rename', onSelect: () => edit({ row, action: 'rename' }) },
    { label: 'Remove', destructive: true, onSelect: () => edit({ row, action: 'remove' }) },
  ]
}

export function ConnectionActionDialog({
  command,
  mutation,
  onClose,
}: {
  command: ConnectionCommand
  mutation: ReturnType<typeof useConnectionAction>
  onClose: () => void
}) {
  const [name, setName] = useState(command.row.display_name)
  const removing = command.action === 'remove'
  const title = removing ? 'Remove connection' : 'Rename connection'
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {removing
              ? `Remove ${command.row.display_name}? Its imported records remain and syncing stops.`
              : `Rename ${command.row.display_name}.`}
          </DialogDescription>
        </DialogHeader>
        {mutation.error && <Alert>{mutation.error.message}</Alert>}
        {!removing && (
          <div className="space-y-2">
            <Label htmlFor="connection-name">Name</Label>
            <Input
              id="connection-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={removing ? 'destructive' : 'default'}
            disabled={mutation.isPending}
            onClick={() => mutation.mutate({ ...command, name }, { onSuccess: onClose })}
          >
            {mutation.isPending ? 'Saving…' : title}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
